import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useStore } from '../store';
import toast from 'react-hot-toast';
import api from '../services/api';

const GOOGLE_CLIENT_ID = '381411494908-budhilu8ujso3kohg4dpo8j2p289ft8c.apps.googleusercontent.com';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { setToken, setUser, token } = useStore();
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [token, navigate]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) return;

    // Decode JWT payload to check domain before sending to backend
    try {
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      if (!payload.email || !payload.email.endsWith('@smalblu.ai')) {
        toast.error('Access restricted to @smalblu.ai accounts only');
        return;
      }
    } catch (e) {
      // Let backend handle validation
    }

    setGoogleLoading(true);
    try {
      const res = await api.post('/auth/google', { credential: response.credential });
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success(`Welcome, ${res.data.user.name}`);
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || 'Google sign-in failed';
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  }, [setToken, setUser, navigate]);

  useEffect(() => {
    // Load Google Identity Services
    if (GOOGLE_CLIENT_ID && !window.google?.accounts) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          hosted_domain: 'smalblu.ai',
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            width: 324,
            text: 'signin_with',
            shape: 'pill',
          });
        }
      };
      document.head.appendChild(script);
    } else if (window.google?.accounts) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        hosted_domain: 'smalblu.ai',
      });
      if (googleBtnRef.current) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          width: 324,
          text: 'signin_with',
          shape: 'pill',
        });
      }
    }
  }, [handleGoogleResponse]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success('Welcome to SmalBlu');
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid credentials';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0a0f1e',
      fontFamily: '"DM Sans", system-ui, sans-serif',
    }}>
      <div style={{
        width: 420,
        padding: 48,
        borderRadius: 20,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(20px)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 16, margin: '0 auto 16px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,132,255,0.3)',
          }}>
            <img
              src="https://framerusercontent.com/images/whCtlttISdJDksSMYxc5sAgsp1A.jpg"
              alt="SmalBlu"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 24, letterSpacing: '-0.5px' }}>SmalBlu</div>
          {/*<div style={{ color: '#4a9eff', fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>Cloud Optimizer</div>*/}
        </div>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@smalblu.ai"
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', fontSize: 14, fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>
          <div style={{ marginBottom: 28 }}>
            <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 10,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', fontSize: 14, fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '13px 20px', borderRadius: 10,
              background: 'linear-gradient(135deg, #0084ff, #0066cc)',
              border: 'none', color: '#fff', fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'opacity 0.15s',
            }}
          >
            <LogIn size={16} />
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '24px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, fontWeight: 500 }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {/* Google Sign-In */}
        {GOOGLE_CLIENT_ID ? (
          <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center' }} />
        ) : (
          <button
            type="button"
            disabled={googleLoading}
            onClick={() => toast('Set GOOGLE_CLIENT_ID in LoginPage.jsx to enable Google Sign-In', { icon: 'ℹ️' })}
            style={{
              width: '100%', padding: '12px 20px', borderRadius: 10,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.08)'; e.target.style.borderColor = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'rgba(255,255,255,0.05)'; e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: 11, marginBottom: 8 }}>© 2026 SmalBlu · Your AI Infrastructure Optimization Team</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
            <a href="mailto:hello@smalblu.ai" style={{ color: 'rgba(74,158,255,0.6)', fontSize: 11, textDecoration: 'none' }}>hello@smalblu.ai</a>
            <a href="https://www.linkedin.com/company/smalblu/" target="_blank" rel="noreferrer" style={{ color: 'rgba(74,158,255,0.6)', fontSize: 11, textDecoration: 'none' }}>LinkedIn</a>
            {/*<a href="https://calendly.com/garv-smalblu" target="_blank" rel="noreferrer" style={{ color: 'rgba(74,158,255,0.6)', fontSize: 11, textDecoration: 'none' }}>Book a Demo</a>*/}
          </div>
        </div>
      </div>
    </div>
  );
}
